"use client";

import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { fetchWithAuth, API_BASE } from "@/lib/auth";

export interface InterestSubmissionItem {
    id: string;
    user_id: string;
    target_community: string;
    user_home_community: string | null;
    selected_options: string[];
    status: string;
    notes?: string | null;
    submitted_at: string;
    user_first_name?: string | null;
    user_last_name?: string | null;
    user_email: string;
    phone?: string | null;
    city?: string | null;
    country?: string | null;
    chapter?: string | null;
    community?: string | null;
    member_status?: string | null;
    program_journey?: string | null;
    membership_journey?: string | null;
    role?: string | null;
    bio?: string | null;
    profile_photo_url?: string | null;
    is_presenter?: boolean | null;
    job_title?: string | null;
    company?: string | null;
}

const ROLE_MAPPING: Record<string, string> = {
    charterholder: "Charterholder",
    charterholderinactive: "Charterholder Inactive",
    level3candidate: "Candidate L3",
    level2candidate: "Candidate L2",
    level1candidate: "Candidate L1",
    l3eligible: "Level 3 Eligible",
    l2eligible: "Level 2 Eligible",
    l1eligible: "Level 1 Eligible",
    professional_member: "Professional Member",
    affiliate_member: "Affiliate Member",
    professional_member_inactive: "Professional Member Inactive",
    affiliate_member_inactive: "Affiliate Member Inactive",
    "Never a Candidate": "Never a Candidate",
    "Never a Member": "Never a Member",
    l3_cleared: "L3 Cleared",
};

const STATUS_CONFIG: Record<
    string,
    { label: string; bg: string; text: string; border: string; dot: string }
> = {
    Pending: {
        label: "Pending",
        bg: "bg-amber-50",
        text: "text-amber-800",
        border: "border-amber-200",
        dot: "bg-amber-500",
    },
    "In Review": {
        label: "In Review",
        bg: "bg-sky-50",
        text: "text-sky-800",
        border: "border-sky-200",
        dot: "bg-sky-500",
    },
    Contacted: {
        label: "Contacted",
        bg: "bg-indigo-50",
        text: "text-indigo-800",
        border: "border-indigo-200",
        dot: "bg-indigo-500",
    },
    Assigned: {
        label: "Assigned",
        bg: "bg-emerald-50",
        text: "text-emerald-800",
        border: "border-emerald-200",
        dot: "bg-emerald-500",
    },
    Archived: {
        label: "Archived",
        bg: "bg-gray-100",
        text: "text-gray-700",
        border: "border-gray-200",
        dot: "bg-gray-400",
    },
};

const ALL_STATUSES = ["Pending", "In Review", "Contacted", "Assigned", "Archived"];

function formatRole(val: string | null | undefined): string {
    if (!val) return "";
    return ROLE_MAPPING[val] || val;
}

function getCandidateBadgeStyle(journey: string | null | undefined) {
    if (!journey || journey === "Never a Candidate") return null;
    const j = journey.toLowerCase();
    if (j.includes("cleared")) {
        return "bg-emerald-50 text-emerald-700 border-emerald-200";
    }
    if (j.includes("candidate")) {
        return "bg-sky-50 text-sky-700 border-sky-200";
    }
    if (j.includes("eligible")) {
        return "bg-indigo-50 text-indigo-700 border-indigo-200";
    }
    return "bg-blue-50 text-blue-700 border-blue-200";
}

function getMembershipBadgeStyle(journey: string | null | undefined) {
    if (!journey || journey === "Never a Member") return null;
    const j = journey.toLowerCase();
    if (j.includes("charterholder")) {
        return "bg-purple-50 text-purple-700 border-purple-200";
    }
    if (j.includes("professional")) {
        return "bg-teal-50 text-teal-700 border-teal-200";
    }
    if (j.includes("affiliate")) {
        return "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200";
    }
    return "bg-gray-100 text-gray-700 border-gray-200";
}

// ── Multi-Select Dropdown ─────────────────────────────────────────────────────

function FilterDropdown({
    label,
    options,
    selected,
    onToggle,
    onClear,
    icon,
}: {
    label: string;
    options: string[];
    selected: Set<string>;
    onToggle: (val: string) => void;
    onClear: () => void;
    icon?: string;
}) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function handler(e: MouseEvent) {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
            }
        }
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const count = selected.size;

    return (
        <div ref={ref} className="relative">
            <button
                type="button"
                onClick={() => setOpen((prev) => !prev)}
                className={`flex items-center gap-2 px-3.5 py-2.5 border rounded-xl text-xs sm:text-sm bg-white focus:outline-none transition-all whitespace-nowrap shadow-xs ${
                    count > 0
                        ? "border-[#003d45] text-[#003d45] font-bold bg-[#003d45]/5"
                        : "border-[#e2e8f0] text-gray-600 hover:border-gray-400"
                }`}
            >
                {icon && <span className="material-symbols-outlined text-[16px] text-gray-400">{icon}</span>}
                <span>{label}</span>
                {count > 0 && (
                    <span className="bg-[#003d45] text-white text-[10px] font-black px-1.5 py-0.5 rounded-full leading-none">
                        {count}
                    </span>
                )}
                <span className="material-symbols-outlined text-[16px] text-gray-400">
                    {open ? "expand_less" : "expand_more"}
                </span>
            </button>

            {open && (
                <div className="absolute top-full left-0 mt-1.5 bg-white border border-[#e2e8f0] rounded-xl shadow-xl z-40 min-w-[220px] max-w-xs py-1.5 overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150">
                    {count > 0 && (
                        <button
                            type="button"
                            onClick={() => {
                                onClear();
                                setOpen(false);
                            }}
                            className="w-full text-left px-3 py-2 text-xs text-[#ae232d] font-bold hover:bg-red-50 transition-colors border-b border-[#f0f0f0] flex items-center gap-1.5"
                        >
                            <span className="material-symbols-outlined text-[14px]">close</span>
                            Clear selection ({count})
                        </button>
                    )}
                    <div className="max-h-60 overflow-y-auto divide-y divide-gray-50">
                        {options.length === 0 ? (
                            <div className="px-3 py-3 text-xs text-gray-400 text-center">No options available</div>
                        ) : (
                            options.map((opt) => {
                                const checked = selected.has(opt);
                                return (
                                    <label
                                        key={opt}
                                        className="flex items-center gap-2.5 px-3 py-2 hover:bg-[#f4f8f8] cursor-pointer transition-colors text-xs text-gray-700"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => onToggle(opt)}
                                            className="accent-[#003d45] w-3.5 h-3.5 rounded flex-shrink-0"
                                        />
                                        <span className={`truncate ${checked ? "font-bold text-[#003d45]" : ""}`}>
                                            {opt}
                                        </span>
                                    </label>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function FilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
    return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#003d45]/10 text-[#003d45] text-xs font-semibold rounded-full border border-[#003d45]/20">
            <span>{label}</span>
            <button
                type="button"
                onClick={onRemove}
                className="hover:text-[#ae232d] transition-colors ml-0.5 rounded-full p-0.5"
                title="Remove filter"
            >
                <span className="material-symbols-outlined text-[12px] block">close</span>
            </button>
        </span>
    );
}

// ── Main Component ────────────────────────────────────────────────────────────

export function VolunteersInterestedPanel() {
    const [submissions, setSubmissions] = useState<InterestSubmissionItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedSub, setSelectedSub] = useState<InterestSubmissionItem | null>(null);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState<string | null>(null);

    // Filter states
    const [search, setSearch] = useState("");
    const [candidateFilter, setCandidateFilter] = useState<string>("all");
    const [membershipFilter, setMembershipFilter] = useState<string>("all");
    const [statusFilter, setStatusFilter] = useState<string>("all");
    const [alignmentFilter, setAlignmentFilter] = useState<string>("all");
    const [selectedCommunities, setSelectedCommunities] = useState<Set<string>>(new Set());
    const [selectedInterests, setSelectedInterests] = useState<Set<string>>(new Set());
    const [sortBy, setSortBy] = useState<"newest" | "oldest" | "name" | "community">("newest");

    // Bulk selection state
    const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
    const [bulkUpdating, setBulkUpdating] = useState(false);

    // Toast feedback
    const [toastMessage, setToastMessage] = useState<string | null>(null);

    const showToast = useCallback((msg: string) => {
        setToastMessage(msg);
        setTimeout(() => setToastMessage(null), 3500);
    }, []);

    const fetchSubmissions = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await fetchWithAuth(`${API_BASE}/api/v1/interest/submissions`);
            if (res.status === 403) {
                setError("You do not have permission to view interest submissions.");
                return;
            }
            if (!res.ok) {
                throw new Error("Failed to fetch interest submissions");
            }
            const data: InterestSubmissionItem[] = await res.json();
            setSubmissions(Array.isArray(data) ? data : []);
        } catch (err: any) {
            console.error(err);
            setError(err?.message || "An error occurred while loading interest submissions.");
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSubmissions();
    }, [fetchSubmissions]);

    // Update single submission status or notes
    const handleUpdateSubmission = async (
        submissionId: string,
        patch: { status?: string; notes?: string }
    ) => {
        setIsUpdatingStatus(submissionId);
        try {
            const res = await fetchWithAuth(`${API_BASE}/api/v1/interest/submissions/${submissionId}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(patch),
            });
            if (!res.ok) throw new Error("Failed to update submission");
            const updated: InterestSubmissionItem = await res.json();

            setSubmissions((prev) => prev.map((item) => (item.id === submissionId ? updated : item)));
            if (selectedSub?.id === submissionId) {
                setSelectedSub(updated);
            }
            showToast("Submission updated successfully");
        } catch (err: any) {
            console.error(err);
            alert("Error updating submission: " + (err?.message || "Unknown error"));
        } finally {
            setIsUpdatingStatus(null);
        }
    };

    // Bulk status update
    const handleBulkUpdateStatus = async (newStatus: string) => {
        if (checkedIds.size === 0) return;
        setBulkUpdating(true);
        try {
            await Promise.all(
                Array.from(checkedIds).map((id) =>
                    fetchWithAuth(`${API_BASE}/api/v1/interest/submissions/${id}`, {
                        method: "PATCH",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ status: newStatus }),
                    })
                )
            );
            setSubmissions((prev) =>
                prev.map((item) => (checkedIds.has(item.id) ? { ...item, status: newStatus } : item))
            );
            if (selectedSub && checkedIds.has(selectedSub.id)) {
                setSelectedSub((prev) => (prev ? { ...prev, status: newStatus } : null));
            }
            showToast(`Updated ${checkedIds.size} submission(s) to "${newStatus}"`);
            setCheckedIds(new Set());
        } catch (err: any) {
            console.error(err);
            alert("Some items could not be updated");
        } finally {
            setBulkUpdating(false);
        }
    };

    // Dynamic distinct filter options
    const allTargetCommunities = useMemo(() => {
        const set = new Set<string>();
        submissions.forEach((s) => {
            if (s.target_community) set.add(s.target_community);
        });
        return Array.from(set).sort();
    }, [submissions]);

    const allInterestOptions = useMemo(() => {
        const set = new Set<string>();
        submissions.forEach((s) => {
            s.selected_options?.forEach((opt) => set.add(opt));
        });
        return Array.from(set).sort();
    }, [submissions]);

    // KPI Summary Calculations
    const stats = useMemo(() => {
        const total = submissions.length;
        const pending = submissions.filter((s) => (s.status || "Pending") === "Pending").length;
        const candidates = submissions.filter((s) => {
            const pj = (s.program_journey || "").toLowerCase();
            return pj && pj !== "never a candidate" && (pj.includes("candidate") || pj.includes("cleared") || pj.includes("eligible"));
        }).length;
        const members = submissions.filter((s) => {
            const mj = (s.membership_journey || "").toLowerCase();
            return mj && mj !== "never a member" && (mj.includes("charterholder") || mj.includes("professional") || mj.includes("affiliate"));
        }).length;
        const communitiesCount = new Set(submissions.map((s) => s.target_community)).size;

        // Calculate most frequent interest
        const counts: Record<string, number> = {};
        submissions.forEach((s) => {
            s.selected_options?.forEach((o) => {
                counts[o] = (counts[o] || 0) + 1;
            });
        });
        let topInterest = "General Volunteer";
        let maxCount = 0;
        Object.entries(counts).forEach(([k, v]) => {
            if (v > maxCount) {
                maxCount = v;
                topInterest = k;
            }
        });

        return {
            total,
            pending,
            candidates,
            members,
            communitiesCount,
            topInterest: maxCount > 0 ? `${topInterest} (${maxCount})` : "None",
        };
    }, [submissions]);

    // Filtering logic
    const filteredSubmissions = useMemo(() => {
        const q = search.trim().toLowerCase();

        return submissions.filter((sub) => {
            // Search filter
            if (q) {
                const hay = [
                    sub.user_first_name,
                    sub.user_last_name,
                    sub.user_email,
                    sub.phone,
                    sub.city,
                    sub.country,
                    sub.target_community,
                    sub.user_home_community,
                    sub.notes,
                    ...(sub.selected_options || []),
                ]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                if (!hay.includes(q)) return false;
            }

            // Candidate / Program Journey filter
            if (candidateFilter !== "all") {
                const pj = (sub.program_journey || "").toLowerCase();
                if (candidateFilter === "all_candidates") {
                    if (!pj || pj === "never a candidate" || (!pj.includes("candidate") && !pj.includes("cleared") && !pj.includes("eligible"))) {
                        return false;
                    }
                } else if (candidateFilter === "l3_cleared") {
                    if (!pj.includes("cleared")) return false;
                } else if (candidateFilter === "level3candidate") {
                    if (!pj.includes("level3") && !pj.includes("candidate l3")) return false;
                } else if (candidateFilter === "level2candidate") {
                    if (!pj.includes("level2") && !pj.includes("candidate l2")) return false;
                } else if (candidateFilter === "level1candidate") {
                    if (!pj.includes("level1") && !pj.includes("candidate l1")) return false;
                } else if (candidateFilter === "eligible") {
                    if (!pj.includes("eligible")) return false;
                } else if (candidateFilter === "non_candidate") {
                    if (pj && pj !== "never a candidate") return false;
                }
            }

            // Membership Journey filter
            if (membershipFilter !== "all") {
                const mj = (sub.membership_journey || "").toLowerCase();
                if (membershipFilter === "charterholder") {
                    if (!mj.includes("charterholder")) return false;
                } else if (membershipFilter === "professional") {
                    if (!mj.includes("professional")) return false;
                } else if (membershipFilter === "affiliate") {
                    if (!mj.includes("affiliate")) return false;
                } else if (membershipFilter === "inactive") {
                    if (!mj.includes("inactive")) return false;
                } else if (membershipFilter === "never") {
                    if (mj && mj !== "never a member") return false;
                }
            }

            // Target community filter
            if (selectedCommunities.size > 0 && !selectedCommunities.has(sub.target_community)) {
                return false;
            }

            // Home alignment filter
            if (alignmentFilter !== "all") {
                const isMatch = sub.user_home_community && sub.target_community && sub.user_home_community.toLowerCase() === sub.target_community.toLowerCase();
                if (alignmentFilter === "matches" && !isMatch) return false;
                if (alignmentFilter === "different" && (!sub.user_home_community || isMatch)) return false;
                if (alignmentFilter === "unknown" && sub.user_home_community) return false;
            }

            // Interests filter
            if (selectedInterests.size > 0) {
                const subOpts = sub.selected_options || [];
                const hasMatch = Array.from(selectedInterests).some((req) => subOpts.includes(req));
                if (!hasMatch) return false;
            }

            // Status filter
            if (statusFilter !== "all") {
                const curStatus = sub.status || "Pending";
                if (curStatus.toLowerCase() !== statusFilter.toLowerCase()) return false;
            }

            return true;
        });
    }, [
        submissions,
        search,
        candidateFilter,
        membershipFilter,
        selectedCommunities,
        alignmentFilter,
        selectedInterests,
        statusFilter,
    ]);

    // Sorting
    const sortedSubmissions = useMemo(() => {
        const items = [...filteredSubmissions];
        items.sort((a, b) => {
            if (sortBy === "newest") {
                return new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime();
            }
            if (sortBy === "oldest") {
                return new Date(a.submitted_at).getTime() - new Date(b.submitted_at).getTime();
            }
            if (sortBy === "name") {
                const nameA = `${a.user_first_name || ""} ${a.user_last_name || ""}`.trim();
                const nameB = `${b.user_first_name || ""} ${b.user_last_name || ""}`.trim();
                return nameA.localeCompare(nameB);
            }
            if (sortBy === "community") {
                return (a.target_community || "").localeCompare(b.target_community || "");
            }
            return 0;
        });
        return items;
    }, [filteredSubmissions, sortBy]);

    // Filter togglers
    const toggleCommunity = (c: string) => {
        setSelectedCommunities((prev) => {
            const next = new Set(prev);
            if (next.has(c)) next.delete(c);
            else next.add(c);
            return next;
        });
    };

    const toggleInterest = (i: string) => {
        setSelectedInterests((prev) => {
            const next = new Set(prev);
            if (next.has(i)) next.delete(i);
            else next.add(i);
            return next;
        });
    };

    const clearAllFilters = () => {
        setSearch("");
        setCandidateFilter("all");
        setMembershipFilter("all");
        setStatusFilter("all");
        setAlignmentFilter("all");
        setSelectedCommunities(new Set());
        setSelectedInterests(new Set());
        setSortBy("newest");
    };

    const hasActiveFilters =
        search !== "" ||
        candidateFilter !== "all" ||
        membershipFilter !== "all" ||
        statusFilter !== "all" ||
        alignmentFilter !== "all" ||
        selectedCommunities.size > 0 ||
        selectedInterests.size > 0 ||
        sortBy !== "newest";

    // Bulk selection checkbox handlers
    const allFilteredSelected =
        sortedSubmissions.length > 0 && sortedSubmissions.every((s) => checkedIds.has(s.id));

    const toggleSelectAll = () => {
        if (allFilteredSelected) {
            setCheckedIds(new Set());
        } else {
            setCheckedIds(new Set(sortedSubmissions.map((s) => s.id)));
        }
    };

    const toggleSelectRow = (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setCheckedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    // CSV Export
    const exportToCSV = () => {
        const rowsToExport = checkedIds.size > 0
            ? sortedSubmissions.filter((s) => checkedIds.has(s.id))
            : sortedSubmissions;

        if (rowsToExport.length === 0) {
            alert("No submissions to export");
            return;
        }

        const headers = [
            "Submission Date",
            "First Name",
            "Last Name",
            "Email",
            "Phone",
            "City",
            "Country",
            "Target Community",
            "Home Community",
            "Location Alignment",
            "Candidate Status / Program",
            "Membership Journey",
            "Interests & Roles",
            "Workflow Status",
            "Staff Notes",
        ];

        const escapeCSV = (val: any) => {
            if (val === null || val === undefined) return '""';
            const str = String(val).replace(/"/g, '""');
            return `"${str}"`;
        };

        const csvRows = [
            headers.join(","),
            ...rowsToExport.map((s) => {
                const date = new Date(s.submitted_at).toLocaleDateString();
                const alignment = s.user_home_community
                    ? s.user_home_community.toLowerCase() === s.target_community.toLowerCase()
                        ? "Matches Target"
                        : "Different Community"
                    : "Unknown";
                const interests = (s.selected_options || []).join("; ");
                const program = formatRole(s.program_journey);
                const membership = formatRole(s.membership_journey);

                return [
                    escapeCSV(date),
                    escapeCSV(s.user_first_name || ""),
                    escapeCSV(s.user_last_name || ""),
                    escapeCSV(s.user_email),
                    escapeCSV(s.phone || ""),
                    escapeCSV(s.city || ""),
                    escapeCSV(s.country || ""),
                    escapeCSV(s.target_community),
                    escapeCSV(s.user_home_community || ""),
                    escapeCSV(alignment),
                    escapeCSV(program),
                    escapeCSV(membership),
                    escapeCSV(interests),
                    escapeCSV(s.status || "Pending"),
                    escapeCSV(s.notes || ""),
                ].join(",");
            }),
        ];

        const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `volunteers_interested_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        showToast(`Exported ${rowsToExport.length} submissions to CSV`);
    };

    return (
        <div className="space-y-6">
            {/* Toast Notification */}
            {toastMessage && (
                <div className="fixed bottom-6 right-6 z-50 bg-[#003d45] text-white px-5 py-3 rounded-xl shadow-2xl flex items-center gap-2.5 text-sm font-semibold animate-in fade-in slide-in-from-bottom-2">
                    <span className="material-symbols-outlined text-[18px] text-emerald-400">check_circle</span>
                    <span>{toastMessage}</span>
                </div>
            )}

            {/* Header section */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <div className="flex items-center gap-2.5">
                        <div className="w-10 h-10 rounded-xl bg-[#003d45]/10 flex items-center justify-center text-[#003d45]">
                            <span className="material-symbols-outlined text-2xl">volunteer_activism</span>
                        </div>
                        <div>
                            <h2 className="text-2xl font-black text-[#003d45] leading-tight">Volunteers Interested</h2>
                            <p className="text-sm text-gray-500">
                                Evaluate, filter, and action member & candidate submissions for chapters and communities.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-2.5 flex-wrap">
                    <button
                        type="button"
                        onClick={fetchSubmissions}
                        disabled={isLoading}
                        className="flex items-center gap-2 px-3.5 py-2.5 border border-[#e2e8f0] bg-white hover:border-[#003d45] hover:text-[#003d45] text-gray-700 rounded-xl text-xs sm:text-sm font-bold transition-all shadow-xs disabled:opacity-50"
                        title="Reload submissions"
                    >
                        <span className={`material-symbols-outlined text-[18px] ${isLoading ? "animate-spin" : ""}`}>
                            refresh
                        </span>
                        <span>Refresh</span>
                    </button>

                    <button
                        type="button"
                        onClick={exportToCSV}
                        className="flex items-center gap-2 px-4 py-2.5 bg-[#003d45] hover:bg-[#007a8a] text-white font-bold text-xs sm:text-sm rounded-xl transition-all shadow-sm"
                        title="Export filtered records to CSV"
                    >
                        <span className="material-symbols-outlined text-[18px]">download</span>
                        <span>Export CSV {checkedIds.size > 0 ? `(${checkedIds.size})` : ""}</span>
                    </button>
                </div>
            </div>

            {/* Top Stat KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <div className="bg-white rounded-2xl border border-[#e2e8f0] p-4 flex flex-col justify-between shadow-xs">
                    <div className="flex items-center justify-between text-gray-400 mb-1">
                        <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Total</span>
                        <span className="material-symbols-outlined text-lg text-[#003d45]">inbox</span>
                    </div>
                    <div>
                        <p className="text-2xl font-black text-[#003d45]">{stats.total}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5 font-medium">Interest submissions</p>
                    </div>
                </div>

                <div
                    onClick={() => setStatusFilter("Pending")}
                    className="bg-white rounded-2xl border border-[#e2e8f0] p-4 flex flex-col justify-between shadow-xs cursor-pointer hover:border-amber-400 transition-colors group"
                >
                    <div className="flex items-center justify-between text-gray-400 mb-1">
                        <span className="text-[11px] font-bold uppercase tracking-wider text-amber-700">Pending</span>
                        <span className="material-symbols-outlined text-lg text-amber-500">pending_actions</span>
                    </div>
                    <div>
                        <p className="text-2xl font-black text-amber-600">{stats.pending}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5 font-medium group-hover:text-amber-600 transition-colors">
                            Click to filter pending →
                        </p>
                    </div>
                </div>

                <div
                    onClick={() => setCandidateFilter("all_candidates")}
                    className="bg-white rounded-2xl border border-[#e2e8f0] p-4 flex flex-col justify-between shadow-xs cursor-pointer hover:border-sky-400 transition-colors group"
                >
                    <div className="flex items-center justify-between text-gray-400 mb-1">
                        <span className="text-[11px] font-bold uppercase tracking-wider text-sky-700">Candidates</span>
                        <span className="material-symbols-outlined text-lg text-sky-600">school</span>
                    </div>
                    <div>
                        <p className="text-2xl font-black text-sky-700">{stats.candidates}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5 font-medium group-hover:text-sky-700 transition-colors">
                            L1 / L2 / L3 / Cleared
                        </p>
                    </div>
                </div>

                <div
                    onClick={() => setMembershipFilter("charterholder")}
                    className="bg-white rounded-2xl border border-[#e2e8f0] p-4 flex flex-col justify-between shadow-xs cursor-pointer hover:border-purple-400 transition-colors group"
                >
                    <div className="flex items-center justify-between text-gray-400 mb-1">
                        <span className="text-[11px] font-bold uppercase tracking-wider text-purple-700">Members</span>
                        <span className="material-symbols-outlined text-lg text-purple-600">badge</span>
                    </div>
                    <div>
                        <p className="text-2xl font-black text-purple-700">{stats.members}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5 font-medium group-hover:text-purple-700 transition-colors">
                            Charterholders & Pro
                        </p>
                    </div>
                </div>

                <div className="bg-white rounded-2xl border border-[#e2e8f0] p-4 flex flex-col justify-between shadow-xs">
                    <div className="flex items-center justify-between text-gray-400 mb-1">
                        <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Target Groups</span>
                        <span className="material-symbols-outlined text-lg text-[#007a8a]">hub</span>
                    </div>
                    <div>
                        <p className="text-2xl font-black text-[#007a8a]">{stats.communitiesCount}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5 font-medium">Unique communities</p>
                    </div>
                </div>

                <div className="bg-white rounded-2xl border border-[#e2e8f0] p-4 flex flex-col justify-between shadow-xs">
                    <div className="flex items-center justify-between text-gray-400 mb-1">
                        <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Top Role</span>
                        <span className="material-symbols-outlined text-lg text-emerald-600">star</span>
                    </div>
                    <div>
                        <p className="text-sm font-bold text-gray-800 line-clamp-1" title={stats.topInterest}>
                            {stats.topInterest}
                        </p>
                        <p className="text-[10px] text-gray-400 mt-0.5 font-medium">Most requested area</p>
                    </div>
                </div>
            </div>

            {/* Filter controls card */}
            <div className="bg-white rounded-2xl border border-[#e2e8f0] p-4 sm:p-5 shadow-xs space-y-3.5">
                {/* Search bar & Primary selects */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
                    {/* Search */}
                    <div className="md:col-span-4 relative">
                        <span className="material-symbols-outlined absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-lg">
                            search
                        </span>
                        <input
                            type="text"
                            placeholder="Search applicant, email, phone, city, notes…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 border border-[#e2e8f0] rounded-xl text-xs sm:text-sm focus:outline-none focus:border-[#007a8a] bg-gray-50/50 hover:bg-white focus:bg-white transition-all"
                        />
                        {search && (
                            <button
                                type="button"
                                onClick={() => setSearch("")}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <span className="material-symbols-outlined text-[16px]">close</span>
                            </button>
                        )}
                    </div>

                    {/* Candidate Level Selector */}
                    <div className="md:col-span-3">
                        <div className="relative">
                            <select
                                value={candidateFilter}
                                onChange={(e) => setCandidateFilter(e.target.value)}
                                className={`w-full py-2.5 px-3 border rounded-xl text-xs sm:text-sm focus:outline-none bg-white transition-all appearance-none cursor-pointer ${
                                    candidateFilter !== "all"
                                        ? "border-[#003d45] text-[#003d45] font-bold bg-[#003d45]/5"
                                        : "border-[#e2e8f0] text-gray-700 hover:border-gray-400"
                                }`}
                            >
                                <option value="all">Candidate Filter (All)</option>
                                <option value="all_candidates">All Program Candidates / Cleared</option>
                                <option value="l3_cleared">L3 Cleared (Exam Complete)</option>
                                <option value="level3candidate">Candidate L3</option>
                                <option value="level2candidate">Candidate L2</option>
                                <option value="level1candidate">Candidate L1</option>
                                <option value="eligible">Eligible (L1/L2/L3)</option>
                                <option value="non_candidate">Non-Candidate</option>
                            </select>
                            <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-base">
                                expand_more
                            </span>
                        </div>
                    </div>

                    {/* Status Selector */}
                    <div className="md:col-span-2">
                        <div className="relative">
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className={`w-full py-2.5 px-3 border rounded-xl text-xs sm:text-sm focus:outline-none bg-white transition-all appearance-none cursor-pointer ${
                                    statusFilter !== "all"
                                        ? "border-[#003d45] text-[#003d45] font-bold bg-[#003d45]/5"
                                        : "border-[#e2e8f0] text-gray-700 hover:border-gray-400"
                                }`}
                            >
                                <option value="all">Status (All)</option>
                                <option value="Pending">Pending</option>
                                <option value="In Review">In Review</option>
                                <option value="Contacted">Contacted</option>
                                <option value="Assigned">Assigned</option>
                                <option value="Archived">Archived</option>
                            </select>
                            <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-base">
                                expand_more
                            </span>
                        </div>
                    </div>

                    {/* Sorting */}
                    <div className="md:col-span-3">
                        <div className="relative">
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value as any)}
                                className="w-full py-2.5 px-3 border border-[#e2e8f0] rounded-xl text-xs sm:text-sm focus:outline-none bg-white text-gray-700 hover:border-gray-400 transition-all appearance-none cursor-pointer"
                            >
                                <option value="newest">Sort: Newest First</option>
                                <option value="oldest">Sort: Oldest First</option>
                                <option value="name">Sort: Member Name (A-Z)</option>
                                <option value="community">Sort: Target Community (A-Z)</option>
                            </select>
                            <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-base">
                                sort
                            </span>
                        </div>
                    </div>
                </div>

                {/* Secondary Filters row */}
                <div className="flex flex-wrap items-center gap-2.5 pt-1 border-t border-gray-100">
                    {/* Membership Filter */}
                    <div className="relative">
                        <select
                            value={membershipFilter}
                            onChange={(e) => setMembershipFilter(e.target.value)}
                            className={`py-2 px-3 border rounded-xl text-xs focus:outline-none bg-white transition-all appearance-none cursor-pointer pr-8 ${
                                membershipFilter !== "all"
                                    ? "border-[#003d45] text-[#003d45] font-bold bg-[#003d45]/5"
                                    : "border-[#e2e8f0] text-gray-600 hover:border-gray-400"
                            }`}
                        >
                            <option value="all">Membership: All</option>
                            <option value="charterholder">Charterholder</option>
                            <option value="professional">Professional Member</option>
                            <option value="affiliate">Affiliate Member</option>
                            <option value="inactive">Inactive Member</option>
                            <option value="never">Never a Member</option>
                        </select>
                        <span className="material-symbols-outlined absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-sm">
                            expand_more
                        </span>
                    </div>

                    {/* Location Alignment */}
                    <div className="relative">
                        <select
                            value={alignmentFilter}
                            onChange={(e) => setAlignmentFilter(e.target.value)}
                            className={`py-2 px-3 border rounded-xl text-xs focus:outline-none bg-white transition-all appearance-none cursor-pointer pr-8 ${
                                alignmentFilter !== "all"
                                    ? "border-[#003d45] text-[#003d45] font-bold bg-[#003d45]/5"
                                    : "border-[#e2e8f0] text-gray-600 hover:border-gray-400"
                            }`}
                        >
                            <option value="all">Alignment: All</option>
                            <option value="matches">Local Match (Target = Home)</option>
                            <option value="different">Out-of-Market (Target ≠ Home)</option>
                            <option value="unknown">Unknown Home Community</option>
                        </select>
                        <span className="material-symbols-outlined absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-sm">
                            expand_more
                        </span>
                    </div>

                    {/* Target Communities MultiSelect */}
                    <FilterDropdown
                        label="Target Community"
                        options={allTargetCommunities}
                        selected={selectedCommunities}
                        onToggle={toggleCommunity}
                        onClear={() => setSelectedCommunities(new Set())}
                        icon="hub"
                    />

                    {/* Interest Roles MultiSelect */}
                    <FilterDropdown
                        label="Interest Roles"
                        options={allInterestOptions}
                        selected={selectedInterests}
                        onToggle={toggleInterest}
                        onClear={() => setSelectedInterests(new Set())}
                        icon="interests"
                    />

                    {/* Clear all button */}
                    {hasActiveFilters && (
                        <button
                            type="button"
                            onClick={clearAllFilters}
                            className="text-xs text-[#ae232d] hover:text-red-700 font-bold hover:underline flex items-center gap-1 ml-auto py-1.5 px-2 rounded-lg hover:bg-red-50 transition-colors"
                        >
                            <span className="material-symbols-outlined text-[14px]">filter_alt_off</span>
                            Reset All Filters
                        </button>
                    )}
                </div>

                {/* Active filter chips */}
                {hasActiveFilters && (
                    <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-100 text-xs">
                        <span className="text-gray-400 font-semibold text-[11px] uppercase tracking-wide mr-1">
                            Active filters:
                        </span>
                        {search && <FilterChip label={`Query: "${search}"`} onRemove={() => setSearch("")} />}
                        {candidateFilter !== "all" && (
                            <FilterChip
                                label={`Candidate: ${candidateFilter.replace("_", " ")}`}
                                onRemove={() => setCandidateFilter("all")}
                            />
                        )}
                        {membershipFilter !== "all" && (
                            <FilterChip
                                label={`Membership: ${membershipFilter}`}
                                onRemove={() => setMembershipFilter("all")}
                            />
                        )}
                        {statusFilter !== "all" && (
                            <FilterChip label={`Status: ${statusFilter}`} onRemove={() => setStatusFilter("all")} />
                        )}
                        {alignmentFilter !== "all" && (
                            <FilterChip
                                label={`Alignment: ${alignmentFilter}`}
                                onRemove={() => setAlignmentFilter("all")}
                            />
                        )}
                        {Array.from(selectedCommunities).map((c) => (
                            <FilterChip key={c} label={`Group: ${c}`} onRemove={() => toggleCommunity(c)} />
                        ))}
                        {Array.from(selectedInterests).map((i) => (
                            <FilterChip key={i} label={`Role: ${i}`} onRemove={() => toggleInterest(i)} />
                        ))}
                    </div>
                )}
            </div>

            {/* Bulk Action Bar (when checkboxes are selected) */}
            {checkedIds.size > 0 && (
                <div className="bg-[#003d45] text-white px-5 py-3.5 rounded-2xl shadow-lg flex flex-wrap items-center justify-between gap-3 animate-in fade-in slide-in-from-top-1 duration-150">
                    <div className="flex items-center gap-3">
                        <span className="bg-white/20 text-white px-2.5 py-1 rounded-full text-xs font-bold">
                            {checkedIds.size} selected
                        </span>
                        <span className="text-sm font-medium text-white/90">Bulk workflow actions:</span>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                        <button
                            type="button"
                            disabled={bulkUpdating}
                            onClick={() => handleBulkUpdateStatus("Contacted")}
                            className="px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold transition-colors disabled:opacity-50"
                        >
                            Mark Contacted
                        </button>
                        <button
                            type="button"
                            disabled={bulkUpdating}
                            onClick={() => handleBulkUpdateStatus("In Review")}
                            className="px-3 py-1.5 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-xs font-bold transition-colors disabled:opacity-50"
                        >
                            Mark In Review
                        </button>
                        <button
                            type="button"
                            disabled={bulkUpdating}
                            onClick={() => handleBulkUpdateStatus("Assigned")}
                            className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold transition-colors disabled:opacity-50"
                        >
                            Mark Assigned
                        </button>
                        <button
                            type="button"
                            disabled={bulkUpdating}
                            onClick={() => handleBulkUpdateStatus("Archived")}
                            className="px-3 py-1.5 rounded-lg bg-gray-600 hover:bg-gray-700 text-white text-xs font-bold transition-colors disabled:opacity-50"
                        >
                            Mark Archived
                        </button>
                        <button
                            type="button"
                            onClick={() => setCheckedIds(new Set())}
                            className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-bold transition-colors"
                        >
                            Clear Selection
                        </button>
                    </div>
                </div>
            )}

            {/* Error banner */}
            {error && (
                <div className="bg-red-50 p-4 rounded-xl border border-red-200 text-red-700 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-red-600">error</span>
                        <span className="text-sm font-medium">{error}</span>
                    </div>
                    <button
                        type="button"
                        onClick={fetchSubmissions}
                        className="text-xs font-bold underline hover:no-underline"
                    >
                        Try again
                    </button>
                </div>
            )}

            {/* Main Submissions Table Card */}
            <div className="bg-white rounded-2xl border border-[#e2e8f0] shadow-xs overflow-hidden">
                <div className="px-6 py-4 border-b border-[#e2e8f0] flex items-center justify-between bg-gray-50/50">
                    <p className="text-xs sm:text-sm font-bold text-gray-700">
                        Showing{" "}
                        <span className="text-[#003d45] font-extrabold">{sortedSubmissions.length}</span> of{" "}
                        {submissions.length} submissions
                    </p>
                    {sortedSubmissions.length > 0 && (
                        <div className="text-xs text-gray-400">
                            Click any row to view full profile & staff action center
                        </div>
                    )}
                </div>

                {isLoading ? (
                    <div className="p-16 text-center space-y-3">
                        <div className="w-9 h-9 border-3 border-[#003d45]/20 border-t-[#003d45] rounded-full animate-spin mx-auto" />
                        <p className="text-sm font-bold text-[#003d45]">Loading volunteer submissions…</p>
                    </div>
                ) : sortedSubmissions.length === 0 ? (
                    <div className="p-16 text-center space-y-3">
                        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto text-gray-400">
                            <span className="material-symbols-outlined text-2xl">person_search</span>
                        </div>
                        <p className="text-base font-bold text-gray-800">No submissions found</p>
                        <p className="text-xs text-gray-500 max-w-sm mx-auto">
                            {hasActiveFilters
                                ? "No submissions match your active filter criteria. Try adjusting or clearing your filters."
                                : "No members have submitted interest expressions yet."}
                        </p>
                        {hasActiveFilters && (
                            <button
                                type="button"
                                onClick={clearAllFilters}
                                className="px-4 py-2 rounded-xl bg-[#003d45] text-white text-xs font-bold hover:bg-[#007a8a] transition-colors"
                            >
                                Clear all filters
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left border-collapse">
                            <thead>
                                <tr className="border-b border-[#e2e8f0] bg-gray-50/80 text-[11px] font-bold uppercase tracking-wider text-gray-500">
                                    <th className="py-3.5 px-4 w-10 text-center">
                                        <input
                                            type="checkbox"
                                            checked={allFilteredSelected}
                                            onChange={toggleSelectAll}
                                            className="accent-[#003d45] w-3.5 h-3.5 rounded cursor-pointer"
                                            title="Select all filtered submissions"
                                        />
                                    </th>
                                    <th className="py-3.5 px-4 font-bold">Date</th>
                                    <th className="py-3.5 px-4 font-bold">Member / Candidate</th>
                                    <th className="py-3.5 px-4 font-bold">Target Group</th>
                                    <th className="py-3.5 px-4 font-bold">Home Alignment</th>
                                    <th className="py-3.5 px-4 font-bold">Interests Expressed</th>
                                    <th className="py-3.5 px-4 font-bold">Status</th>
                                    <th className="py-3.5 px-4 text-right font-bold">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[#e2e8f0]">
                                {sortedSubmissions.map((sub) => {
                                    const isSelected = checkedIds.has(sub.id);
                                    const statusCfg = STATUS_CONFIG[sub.status] || STATUS_CONFIG.Pending;
                                    const candidateBadgeCls = getCandidateBadgeStyle(sub.program_journey);
                                    const membershipBadgeCls = getMembershipBadgeStyle(sub.membership_journey);
                                    const isMatch =
                                        sub.user_home_community &&
                                        sub.target_community &&
                                        sub.user_home_community.toLowerCase() === sub.target_community.toLowerCase();

                                    const initials = `${sub.user_first_name?.[0] || ""}${
                                        sub.user_last_name?.[0] || ""
                                    }`.toUpperCase();

                                    return (
                                        <tr
                                            key={sub.id}
                                            onClick={() => setSelectedSub(sub)}
                                            className={`transition-colors cursor-pointer group ${
                                                isSelected ? "bg-[#003d45]/5" : "hover:bg-gray-50/80 bg-white"
                                            }`}
                                        >
                                            {/* Checkbox */}
                                            <td className="py-4 px-4 text-center" onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={(e) => toggleSelectRow(sub.id, e as any)}
                                                    className="accent-[#003d45] w-3.5 h-3.5 rounded cursor-pointer"
                                                />
                                            </td>

                                            {/* Date */}
                                            <td className="py-4 px-4 whitespace-nowrap">
                                                <div className="font-semibold text-gray-800 text-xs">
                                                    {new Date(sub.submitted_at).toLocaleDateString()}
                                                </div>
                                                <div className="text-[10px] text-gray-400 mt-0.5">
                                                    {new Date(sub.submitted_at).toLocaleTimeString([], {
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                    })}
                                                </div>
                                            </td>

                                            {/* Member & Credentials */}
                                            <td className="py-4 px-4 min-w-[240px]">
                                                <div className="flex items-start gap-3">
                                                    {sub.profile_photo_url ? (
                                                        <img
                                                            src={sub.profile_photo_url}
                                                            alt={initials}
                                                            className="w-9 h-9 rounded-full object-cover border border-gray-200 flex-shrink-0"
                                                        />
                                                    ) : (
                                                        <div className="w-9 h-9 rounded-full bg-[#003d45] text-white flex items-center justify-center text-xs font-black flex-shrink-0">
                                                            {initials || "U"}
                                                        </div>
                                                    )}
                                                    <div className="min-w-0">
                                                        <div className="font-bold text-gray-900 group-hover:text-[#007a8a] transition-colors leading-tight">
                                                            {sub.user_first_name} {sub.user_last_name}
                                                        </div>
                                                        <div className="text-xs text-gray-500 truncate mt-0.5">
                                                            {sub.user_email}
                                                        </div>
                                                        {sub.city && (
                                                            <div className="text-[11px] text-gray-400 flex items-center gap-1 mt-0.5">
                                                                <span className="material-symbols-outlined text-[12px]">
                                                                    location_on
                                                                </span>
                                                                <span>
                                                                    {sub.city}
                                                                    {sub.country ? `, ${sub.country}` : ""}
                                                                </span>
                                                            </div>
                                                        )}

                                                        {/* Badges for Candidate / Journey */}
                                                        <div className="flex flex-wrap gap-1 mt-1.5">
                                                            {candidateBadgeCls && (
                                                                <span
                                                                    className={`px-2 py-0.5 rounded text-[10px] font-bold border ${candidateBadgeCls}`}
                                                                >
                                                                    {formatRole(sub.program_journey)}
                                                                </span>
                                                            )}
                                                            {membershipBadgeCls && (
                                                                <span
                                                                    className={`px-2 py-0.5 rounded text-[10px] font-bold border ${membershipBadgeCls}`}
                                                                >
                                                                    {formatRole(sub.membership_journey)}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Target Group */}
                                            <td className="py-4 px-4 whitespace-nowrap">
                                                <div className="font-bold text-gray-800 text-xs flex items-center gap-1.5">
                                                    <span className="material-symbols-outlined text-[16px] text-[#007a8a]">
                                                        groups
                                                    </span>
                                                    <span>{sub.target_community}</span>
                                                </div>
                                            </td>

                                            {/* Home Community Alignment */}
                                            <td className="py-4 px-4 whitespace-nowrap">
                                                {sub.user_home_community ? (
                                                    isMatch ? (
                                                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                                            <span className="material-symbols-outlined text-[13px]">
                                                                check_circle
                                                            </span>
                                                            Matches target
                                                        </span>
                                                    ) : (
                                                        <div className="text-xs">
                                                            <span className="font-medium text-gray-700">
                                                                {sub.user_home_community}
                                                            </span>
                                                            <span className="block text-[10px] text-amber-600 font-semibold">
                                                                Out of market
                                                            </span>
                                                        </div>
                                                    )
                                                ) : (
                                                    <span className="text-xs text-gray-400 italic">Unknown</span>
                                                )}
                                            </td>

                                            {/* Interests & Roles */}
                                            <td className="py-4 px-4 max-w-xs">
                                                <div className="flex flex-wrap gap-1">
                                                    {(sub.selected_options || []).map((opt, idx) => (
                                                        <span
                                                            key={idx}
                                                            className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded text-[10px] font-medium border border-gray-200"
                                                        >
                                                            {opt}
                                                        </span>
                                                    ))}
                                                </div>
                                                {sub.notes && (
                                                    <div className="mt-1 text-[11px] text-gray-500 italic flex items-center gap-1">
                                                        <span className="material-symbols-outlined text-[12px] text-amber-600">
                                                            sticky_note_2
                                                        </span>
                                                        <span className="truncate max-w-[200px]">{sub.notes}</span>
                                                    </div>
                                                )}
                                            </td>

                                            {/* Inline Status Dropdown */}
                                            <td className="py-4 px-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                                <div className="relative inline-block">
                                                    <select
                                                        value={sub.status || "Pending"}
                                                        disabled={isUpdatingStatus === sub.id}
                                                        onChange={(e) =>
                                                            handleUpdateSubmission(sub.id, { status: e.target.value })
                                                        }
                                                        className={`text-xs font-bold px-2.5 py-1 rounded-full border cursor-pointer focus:outline-none transition-all pr-6 appearance-none ${statusCfg.bg} ${statusCfg.text} ${statusCfg.border}`}
                                                    >
                                                        {ALL_STATUSES.map((st) => (
                                                            <option key={st} value={st}>
                                                                {st}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <span className="material-symbols-outlined absolute right-1.5 top-1/2 -translate-y-1/2 text-xs pointer-events-none opacity-60">
                                                        expand_more
                                                    </span>
                                                </div>
                                            </td>

                                            {/* Action Button */}
                                            <td className="py-4 px-4 text-right whitespace-nowrap">
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedSub(sub);
                                                    }}
                                                    className="px-3 py-1.5 rounded-xl border border-[#e2e8f0] text-xs font-bold text-gray-700 hover:bg-[#003d45] hover:text-white hover:border-[#003d45] transition-all shadow-2xs"
                                                >
                                                    Review & Act
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Slide-over Detail Drawer / Modal for Actioning the Submission */}
            {selectedSub && (
                <DetailDrawer
                    sub={selectedSub}
                    onClose={() => setSelectedSub(null)}
                    onUpdate={handleUpdateSubmission}
                    isUpdating={isUpdatingStatus === selectedSub.id}
                    onToast={showToast}
                />
            )}
        </div>
    );
}

// ── Detail Drawer / Modal ─────────────────────────────────────────────────────

function DetailDrawer({
    sub,
    onClose,
    onUpdate,
    isUpdating,
    onToast,
}: {
    sub: InterestSubmissionItem;
    onClose: () => void;
    onUpdate: (submissionId: string, patch: { status?: string; notes?: string }) => Promise<void>;
    isUpdating: boolean;
    onToast: (msg: string) => void;
}) {
    const [localStatus, setLocalStatus] = useState(sub.status || "Pending");
    const [localNotes, setLocalNotes] = useState(sub.notes || "");
    const [copiedEmail, setCopiedEmail] = useState(false);

    useEffect(() => {
        setLocalStatus(sub.status || "Pending");
        setLocalNotes(sub.notes || "");
    }, [sub]);

    const handleSave = async () => {
        await onUpdate(sub.id, {
            status: localStatus,
            notes: localNotes,
        });
    };

    const copyContactDetails = () => {
        const text = `Member: ${sub.user_first_name} ${sub.user_last_name}
Email: ${sub.user_email}
Phone: ${sub.phone || "N/A"}
Target Group: ${sub.target_community}
Home Community: ${sub.user_home_community || "Unknown"}
Candidate Journey: ${formatRole(sub.program_journey) || "N/A"}
Membership Journey: ${formatRole(sub.membership_journey) || "N/A"}
Interests: ${(sub.selected_options || []).join(", ")}`;

        navigator.clipboard.writeText(text);
        setCopiedEmail(true);
        onToast("Contact details copied to clipboard!");
        setTimeout(() => setCopiedEmail(false), 2000);
    };

    const isMatch =
        sub.user_home_community &&
        sub.target_community &&
        sub.user_home_community.toLowerCase() === sub.target_community.toLowerCase();

    const candidateBadgeCls = getCandidateBadgeStyle(sub.program_journey);
    const membershipBadgeCls = getMembershipBadgeStyle(sub.membership_journey);
    const initials = `${sub.user_first_name?.[0] || ""}${sub.user_last_name?.[0] || ""}`.toUpperCase();

    // Draft email body
    const emailSubject = encodeURIComponent(`CMT Association - Volunteer Interest in ${sub.target_community}`);
    const emailBody = encodeURIComponent(`Hi ${sub.user_first_name || "there"},

Thank you for expressing interest in getting involved with the ${sub.target_community} with the CMT Association!

We noticed you are interested in:
${(sub.selected_options || []).map((o) => `• ${o}`).join("\n")}

We would love to connect and discuss how you can help support our chapter and local community members.

Best regards,
CMT Association Volunteer Committee`);

    const mailtoUrl = `mailto:${sub.user_email}?subject=${emailSubject}&body=${emailBody}`;

    return (
        <div className="fixed inset-0 z-50 overflow-hidden bg-black/40 backdrop-blur-xs flex justify-end animate-in fade-in duration-200">
            <div
                className="w-full max-w-xl bg-white h-full shadow-2xl flex flex-col justify-between overflow-y-auto animate-in slide-in-from-right duration-250 border-l border-gray-200"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="p-6 border-b border-gray-200 bg-gray-50/70 flex items-start justify-between">
                    <div className="flex items-center gap-3.5">
                        {sub.profile_photo_url ? (
                            <img
                                src={sub.profile_photo_url}
                                alt={initials}
                                className="w-14 h-14 rounded-2xl object-cover border-2 border-white shadow-sm"
                            />
                        ) : (
                            <div className="w-14 h-14 rounded-2xl bg-[#003d45] text-white flex items-center justify-center text-lg font-black shadow-sm">
                                {initials || "U"}
                            </div>
                        )}
                        <div>
                            <h3 className="text-xl font-black text-[#003d45]">
                                {sub.user_first_name} {sub.user_last_name}
                            </h3>
                            <p className="text-xs text-gray-500 font-medium">{sub.user_email}</p>
                            <div className="flex flex-wrap gap-1.5 mt-1.5">
                                {candidateBadgeCls && (
                                    <span className={`px-2 py-0.5 rounded text-[11px] font-bold border ${candidateBadgeCls}`}>
                                        {formatRole(sub.program_journey)}
                                    </span>
                                )}
                                {membershipBadgeCls && (
                                    <span className={`px-2 py-0.5 rounded text-[11px] font-bold border ${membershipBadgeCls}`}>
                                        {formatRole(sub.membership_journey)}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-700 p-2 rounded-xl hover:bg-gray-200/60 transition-colors"
                        title="Close panel"
                    >
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                {/* Body Content */}
                <div className="p-6 space-y-6 flex-1">
                    {/* Quick Action Strip */}
                    <div className="grid grid-cols-2 gap-2.5">
                        <a
                            href={mailtoUrl}
                            className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-[#003d45] hover:bg-[#007a8a] text-white text-xs font-bold transition-colors shadow-2xs"
                        >
                            <span className="material-symbols-outlined text-[16px]">mail</span>
                            <span>Email Member</span>
                        </a>

                        <button
                            type="button"
                            onClick={copyContactDetails}
                            className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl border border-gray-200 hover:border-[#003d45] hover:text-[#003d45] text-gray-700 text-xs font-bold transition-colors bg-white shadow-2xs"
                        >
                            <span className="material-symbols-outlined text-[16px]">
                                {copiedEmail ? "check" : "content_copy"}
                            </span>
                            <span>{copiedEmail ? "Copied!" : "Copy Details"}</span>
                        </button>
                    </div>

                    {/* Section: Expression of Interest Details */}
                    <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100 space-y-3">
                        <h4 className="text-xs font-black uppercase tracking-wider text-[#003d45] flex items-center gap-1.5">
                            <span className="material-symbols-outlined text-base">interests</span>
                            <span>Expressed Interest Details</span>
                        </h4>

                        <div className="grid grid-cols-2 gap-4 text-xs pt-1">
                            <div>
                                <span className="text-gray-400 block font-semibold text-[10px] uppercase">
                                    Target Group
                                </span>
                                <span className="font-bold text-gray-900 text-sm">{sub.target_community}</span>
                            </div>

                            <div>
                                <span className="text-gray-400 block font-semibold text-[10px] uppercase">
                                    Submitted On
                                </span>
                                <span className="font-semibold text-gray-800">
                                    {new Date(sub.submitted_at).toLocaleString()}
                                </span>
                            </div>

                            <div>
                                <span className="text-gray-400 block font-semibold text-[10px] uppercase">
                                    Home Community
                                </span>
                                <span className="font-semibold text-gray-800">
                                    {sub.user_home_community || "Unknown"}
                                </span>
                            </div>

                            <div>
                                <span className="text-gray-400 block font-semibold text-[10px] uppercase">
                                    Location Match
                                </span>
                                {sub.user_home_community ? (
                                    isMatch ? (
                                        <span className="text-emerald-700 font-bold flex items-center gap-1 mt-0.5">
                                            <span className="material-symbols-outlined text-sm">check_circle</span>
                                            Local match
                                        </span>
                                    ) : (
                                        <span className="text-amber-700 font-bold flex items-center gap-1 mt-0.5">
                                            <span className="material-symbols-outlined text-sm">warning</span>
                                            Out of market
                                        </span>
                                    )
                                ) : (
                                    <span className="text-gray-400">Not recorded</span>
                                )}
                            </div>
                        </div>

                        {/* Roles */}
                        <div className="pt-2 border-t border-gray-200">
                            <span className="text-gray-400 block font-semibold text-[10px] uppercase mb-2">
                                Roles & Areas of Interest:
                            </span>
                            <div className="flex flex-wrap gap-1.5">
                                {(sub.selected_options || []).map((opt, idx) => (
                                    <span
                                        key={idx}
                                        className="bg-white text-gray-800 px-3 py-1 rounded-lg text-xs font-semibold border border-gray-200 shadow-2xs flex items-center gap-1"
                                    >
                                        <span className="material-symbols-outlined text-[14px] text-[#007a8a]">
                                            check
                                        </span>
                                        {opt}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Section: Candidate & Member Background */}
                    <div className="bg-white rounded-2xl p-4 border border-gray-200 space-y-3">
                        <h4 className="text-xs font-black uppercase tracking-wider text-[#003d45] flex items-center gap-1.5">
                            <span className="material-symbols-outlined text-base">person</span>
                            <span>Member Background</span>
                        </h4>

                        <div className="grid grid-cols-2 gap-3 text-xs">
                            <div>
                                <span className="text-gray-400 block font-medium text-[10px] uppercase">Phone</span>
                                <span className="font-semibold text-gray-800">{sub.phone || "Not provided"}</span>
                            </div>
                            <div>
                                <span className="text-gray-400 block font-medium text-[10px] uppercase">Location</span>
                                <span className="font-semibold text-gray-800">
                                    {[sub.city, sub.country].filter(Boolean).join(", ") || "Unknown"}
                                </span>
                            </div>
                            <div>
                                <span className="text-gray-400 block font-medium text-[10px] uppercase">
                                    Current Chapter
                                </span>
                                <span className="font-semibold text-gray-800">{sub.chapter || "None"}</span>
                            </div>
                            <div>
                                <span className="text-gray-400 block font-medium text-[10px] uppercase">
                                    Member Status
                                </span>
                                <span className="font-semibold text-gray-800">{sub.member_status || "Standard"}</span>
                            </div>
                        </div>

                        {sub.bio && (
                            <div className="pt-2 border-t border-gray-100">
                                <span className="text-gray-400 block font-medium text-[10px] uppercase mb-1">Bio</span>
                                <p className="text-xs text-gray-700 leading-relaxed bg-gray-50 p-2.5 rounded-xl border border-gray-100">
                                    {sub.bio}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Section: Staff Workflow Management */}
                    <div className="bg-amber-50/50 rounded-2xl p-4 border border-amber-200/60 space-y-4">
                        <h4 className="text-xs font-black uppercase tracking-wider text-amber-900 flex items-center gap-1.5">
                            <span className="material-symbols-outlined text-base text-amber-700">task_alt</span>
                            <span>Staff Workflow & Actions</span>
                        </h4>

                        {/* Status selector */}
                        <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                                Pipeline Status
                            </label>
                            <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5">
                                {ALL_STATUSES.map((st) => {
                                    const active = localStatus === st;
                                    const cfg = STATUS_CONFIG[st];
                                    return (
                                        <button
                                            key={st}
                                            type="button"
                                            onClick={() => setLocalStatus(st)}
                                            className={`py-2 px-1 rounded-xl text-xs font-bold border transition-all text-center ${
                                                active
                                                    ? `${cfg.bg} ${cfg.text} ${cfg.border} ring-2 ring-[#003d45]/20 shadow-xs`
                                                    : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
                                            }`}
                                        >
                                            {st}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Staff Notes */}
                        <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                                Staff Notes & Follow-up Memo
                            </label>
                            <textarea
                                rows={3}
                                value={localNotes}
                                onChange={(e) => setLocalNotes(e.target.value)}
                                placeholder="Add internal notes on candidate suitability, follow-up calls, or chapter placement…"
                                className="w-full p-3 border border-gray-300 rounded-xl text-xs sm:text-sm bg-white focus:outline-none focus:border-[#007a8a] focus:ring-1 focus:ring-[#007a8a] resize-none"
                            />
                        </div>
                    </div>
                </div>

                {/* Footer Buttons */}
                <div className="p-6 border-t border-gray-200 bg-gray-50 flex items-center gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex-1 py-3 px-4 border border-gray-300 rounded-xl text-xs sm:text-sm font-bold text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                        Close
                    </button>
                    <button
                        type="button"
                        disabled={isUpdating}
                        onClick={handleSave}
                        className="flex-1 py-3 px-4 bg-[#003d45] hover:bg-[#007a8a] text-white rounded-xl text-xs sm:text-sm font-black transition-colors disabled:opacity-50 flex items-center justify-center gap-2 shadow-sm"
                    >
                        {isUpdating ? (
                            <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                        ) : (
                            <span className="material-symbols-outlined text-base">save</span>
                        )}
                        <span>Save Changes</span>
                    </button>
                </div>
            </div>
        </div>
    );
}
